import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, Signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ARCConfig, BetslipUpsellConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, distinctUntilChanged, map, take, tap } from 'rxjs';

import { FeatureLocation, IARCProfileConfiguration } from '../../../lib/arc/arc.model';
import { BetslipType } from '../../core/betslip-type';
import { BetslipPlaceBetActions } from '../../modules/betplacement/actions';
import { PlaceButtonActionHandlerService } from '../../modules/betplacement/services/place-button/place-button-action-handler.service';
import {
    PLACE_BUTTON_MESSAGES_PROVIDER,
    PlaceButtonMessagesProviderService,
} from '../../modules/betplacement/services/place-button/place-button-messages-provider.service';
import {
    IPlaceButtonStateService,
    PLACE_BUTTON_ACTION_HANDLER_SERVICE,
    PLACE_BUTTON_ACTION_SERVICE,
    PLACE_BUTTON_DISABLE_SERVICE,
    PLACE_BUTTON_MESSAGE_SERVICE,
    PLACE_BUTTON_STATE_SERVICE,
    PLACE_BUTTON_TITLE_SERVICE,
    PLACE_BUTTON_VIEWMODEL_SERVICE,
} from '../../modules/betplacement/services/place-button/viewmodel/interfaces/place-button-interfaces';
import { BetslipPlaceButtonActionService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-action.service';
import { BetslipPlaceButtonDisableService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-disable.service';
import { BetslipPlaceButtonMessageService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-message.service';
import { BetslipPlaceButtonStateService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-state.service';
import { BetslipPlaceButtonTitleService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-title.service';
import { BetslipPlaceButtonViewModelService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-viewmodel.service';
import { IPlaceButtonState } from '../../modules/betplacement/state';
import { BetslipOnboardingMessageService, OnboardingMessage } from '../../modules/onboarding/betslip-onboarding-message.service';
import { RemoveButtonStyle } from '../../modules/picks/components/digital/pick-remove-button/remove-button-style';
import { betslipPicksListSelector } from '../../modules/picks/selectors';
import { QuickDepositSource } from '../../modules/quick-deposit/quick-deposit.models';
import { IQuickDepositState } from '../../modules/quick-deposit/quick-deposit.state';
import { quickDepositSelector } from '../../modules/quick-deposit/selector';
import { QuickDepositService } from '../../modules/quick-deposit/services/quick-deposit.service';
import { selectShowRewardsNotEligibleMessage } from '../../modules/reward-tokens/selectors';
import { settingsOpenSelector } from '../../modules/settings/digital-selectors';
import { RemoveButtonPosition } from '../../modules/single-bet/remove-button-position-enum';
import { betslipTypesBaseSelector } from '../../modules/types/base/selectors';
import { IBetslipDigitalRootState } from '../state';

@Component({
    selector: 'bs-betslip-edit-state',
    templateUrl: './betslip-edit-state.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./betslip-edit-state.component.scss'],
    providers: [
        { provide: PLACE_BUTTON_VIEWMODEL_SERVICE, useClass: BetslipPlaceButtonViewModelService },
        { provide: PLACE_BUTTON_ACTION_SERVICE, useClass: BetslipPlaceButtonActionService },
        { provide: PLACE_BUTTON_ACTION_HANDLER_SERVICE, useClass: PlaceButtonActionHandlerService },
        { provide: PLACE_BUTTON_MESSAGE_SERVICE, useClass: BetslipPlaceButtonMessageService },
        { provide: PLACE_BUTTON_TITLE_SERVICE, useClass: BetslipPlaceButtonTitleService },
        { provide: PLACE_BUTTON_DISABLE_SERVICE, useClass: BetslipPlaceButtonDisableService },
        { provide: PLACE_BUTTON_STATE_SERVICE, useClass: BetslipPlaceButtonStateService },
        { provide: PLACE_BUTTON_MESSAGES_PROVIDER, useClass: PlaceButtonMessagesProviderService },
    ],
    standalone: false,
})
export class BetslipEditStateComponent {
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() inColumn = false;
    @Input() isUserOnPlayBreak = false;
    @Input() profileData$: Observable<IARCProfileConfiguration>;
    @Input() hasArcPreBetMessage$: Observable<boolean>;
    @ViewChild('scroller') scroller: ElementRef;
    RemoveButtonPosition = RemoveButtonPosition;
    RemoveButtonStyle = RemoveButtonStyle;

    isTeaserBet = false;
    betslipTypes = BetslipType;

    tabProps$: Observable<{
        selectedBetslipType: BetslipType | null;
        activeBetslipTypes: BetslipType[];
    }> = this.store.select(betslipTypesBaseSelector).pipe(
        map((state) => ({
            selectedBetslipType: state.currentSelectedType,
            activeBetslipTypes: state.activeTypes,
        })),
        tap((state) => (this.isTeaserBet = state.selectedBetslipType === BetslipType.Teaser)),
    );

    pickList$ = this.store.select(betslipPicksListSelector);

    contentViewModel$ = combineLatest({
        tabProps: this.tabProps$,
        pickList: this.pickList$,
    });

    isSettingsOpened$: Observable<boolean>;
    onboardingMessage$: Observable<OnboardingMessage | null>;
    quickDepositState$: Observable<IQuickDepositState>;
    quickDepositOpen: boolean;
    optionsExpanded = false;
    readonly quickDepositSource = QuickDepositSource.Betslip;
    placeButtonState: IPlaceButtonState;
    featureLocation = FeatureLocation;
    hasComboPreventionError = false;
    hasNewCustomerOfferPick = false;
    hasOnboarding: boolean;
    isPanicSliderEnabled: boolean;
    rewardTokensNotEligibleMessage: string;
    showRewardsNotEligibleMessage: Signal<boolean>;

    constructor(
        private timerService: TimerService,
        public betslipUpsellConfig: BetslipUpsellConfig,
        private store: Store<IBetslipDigitalRootState>,
        private betslipOnboardingMessageService: BetslipOnboardingMessageService,
        @Inject(PLACE_BUTTON_STATE_SERVICE) private placeButtonService: IPlaceButtonStateService,
        public quickDepositService: QuickDepositService,
        public userService: UserService,
        changeDetector: ChangeDetectorRef,
        private aRCConfig: ARCConfig,
        private sitecore: Sitecore,
    ) {
        this.isSettingsOpened$ = store.select(settingsOpenSelector);
        this.quickDepositState$ = store.select(quickDepositSelector);
        this.onboardingMessage$ = this.betslipOnboardingMessageService.onboardingMessage$.pipe(tap((t) => (this.hasOnboarding = !!t)));
        this.quickDepositState$
            .pipe(
                map((state) => state.isOpen || state.isVisible),
                distinctUntilChanged(),
                takeUntilDestroyed(),
            )
            .subscribe((open) => {
                this.quickDepositOpen = open;

                const invalid = !quickDepositService.isAvailable && open;

                store.dispatch(BetslipPlaceBetActions.validatePlaceButton({ isValid: !invalid }));
            });
        this.placeButtonService.state$.pipe(takeUntilDestroyed()).subscribe((state) => {
            this.placeButtonState = state;
            this.hasComboPreventionError = this.placeButtonState.hasComboPreventionError;
            changeDetector.markForCheck();
        });

        store
            .select(betslipPicksListSelector)
            .pipe(takeUntilDestroyed())
            .subscribe((picks) => (this.hasNewCustomerOfferPick = picks.some((pick) => pick.isNewCustomerOfferPick)));

        this.isPanicSliderEnabled = this.aRCConfig.showSelfExclusionLock;
        this.showRewardsNotEligibleMessage = store.selectSignal(selectShowRewardsNotEligibleMessage);
        this.rewardTokensNotEligibleMessage = this.sitecore.crmTokens.messages.RewardsNotEligibleContent;
    }

    onSettingsOpened($event: number) {
        this.timerService
            .timer(1)
            .pipe(take(1))
            .subscribe(() => {
                if (this.scroller) {
                    if ($event === 0) {
                        this.scroller.nativeElement.style = undefined;
                    } else {
                        this.scroller.nativeElement.style.minHeight = `${$event}px`;
                    }
                }
            });
    }

    get isProcessing(): boolean {
        return this.placeButtonState?.isProcessing ?? false;
    }
}
